import { format } from "date-fns";

import { AccountStatus } from "@/generated/model/accountStatus";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import { FitToWorkStatus } from "@/generated/model/fitToWorkStatus";
import { formatUkDate, parseApiDateToLocalMidnight } from "@/lib/date-utils";

export const NOT_RECORDED = "Not recorded";

const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  [EmploymentStatus.FULL_TIME]: "Full time",
  [EmploymentStatus.PART_TIME]: "Part time",
  [EmploymentStatus.BANK]: "Bank",
  [EmploymentStatus.ON_LEAVE]: "On leave",
  [EmploymentStatus.LEFT]: "Left",
};

const FIT_TO_WORK_LABELS: Record<FitToWorkStatus, string> = {
  [FitToWorkStatus.FIT]: "Fit to work",
  [FitToWorkStatus.FIT_WITH_ADJUSTMENTS]: "Fit with adjustments",
  [FitToWorkStatus.NOT_FIT]: "Not fit to work",
};

const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  [AccountStatus.ACTIVE]: "Active",
  [AccountStatus.DEACTIVATED]: "Deactivated",
};

// The lookups fall back to the raw value: the server can add a value before
// this bundle knows it, and a crash is worse than an unfriendly label.

export function employmentStatusLabel(
  status: EmploymentStatus | null | undefined,
): string {
  if (!status) return NOT_RECORDED;
  return EMPLOYMENT_STATUS_LABELS[status] ?? status;
}

export function fitToWorkLabel(
  status: FitToWorkStatus | null | undefined,
): string {
  if (!status) return NOT_RECORDED;
  return FIT_TO_WORK_LABELS[status] ?? status;
}

export function accountStatusLabel(status: AccountStatus): string {
  return ACCOUNT_STATUS_LABELS[status] ?? status;
}

export function textOrNotRecorded(value: string | null | undefined): string {
  if (value === null || value === undefined) return NOT_RECORDED;
  if (value.trim() === "") return NOT_RECORDED;
  return value;
}

/** A form's text as the API takes it: trimmed, and null when blank. */
export function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function yesNo(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return NOT_RECORDED;
  return value ? "Yes" : "No";
}

/** A "YYYY-MM-DD" date-only value, shown without any time zone shift. */
export function formatProfileDate(value: string | null | undefined): string {
  if (!value) return NOT_RECORDED;
  return format(parseApiDateToLocalMidnight(value), "d MMM yyyy");
}

/** An ISO date-time, shown in UK time. */
export function formatProfileTimestamp(
  value: string | null | undefined,
): string {
  if (!value) return NOT_RECORDED;
  return formatUkDate(new Date(value), "d MMM yyyy, h:mm a");
}

/** An ISO date-time as its UK day, e.g. a banner's "last updated". */
export function formatProfileDay(value: string | null | undefined): string {
  if (!value) return NOT_RECORDED;
  return formatUkDate(new Date(value), "d MMM yyyy");
}

/** "{when} by {who}", or Not recorded before the first save. */
export function lastUpdatedLabel(
  at: string | null | undefined,
  by: { name: string | null } | null | undefined,
): string {
  if (!at) return NOT_RECORDED;
  return [formatProfileTimestamp(at), by?.name].filter(Boolean).join(" by ");
}

/** Anyone not marked Left counts as active, including no status at all. */
export function isFormerEmployee(
  status: EmploymentStatus | null | undefined,
): boolean {
  return status === EmploymentStatus.LEFT;
}

export function employmentBadgeLabel(
  status: EmploymentStatus | null | undefined,
): string {
  return isFormerEmployee(status) ? "Former Employee" : "Active Employee";
}

export function employmentBadgeVariant(
  status: EmploymentStatus | null | undefined,
): "neutral" | "brand" | "success" {
  if (isFormerEmployee(status)) return "neutral";
  if (status === EmploymentStatus.ON_LEAVE) return "brand";
  return "success";
}

/** "Ravi Patel (Brother)". */
export function emergencyContactName(
  name: string | null,
  relationship: string | null,
): string {
  const label = [name, relationship && `(${relationship})`]
    .filter(Boolean)
    .join(" ");
  return textOrNotRecorded(label);
}

export const JOB_AND_ROOM_NOT_SET = "Job title and room not set";

export function jobAndRoomLine(
  jobTitle: string | null | undefined,
  roomName: string | null | undefined,
): string {
  const parts = [jobTitle, roomName].filter((part) => part?.trim());
  if (parts.length === 0) return JOB_AND_ROOM_NOT_SET;
  return parts.join(" · ");
}

/** The address to reach someone at: work first. The login email is not a contact address. */
export function primaryContactEmail(contactDetails: {
  workEmail: string | null;
  personalEmail: string | null;
}): string {
  return textOrNotRecorded(
    contactDetails.workEmail || contactDetails.personalEmail,
  );
}

export function fullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}

export function initialsOf(name: string): string {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return initials || "?";
}

/**
 * True for the 404 the API returns for every profile the caller may not see,
 * and for the 400 of a malformed id in the URL, which retrying cannot fix.
 */
export function isProfileUnavailable(error: unknown): boolean {
  const e = error as { response?: { status?: number }; status?: number };
  const status = e?.response?.status ?? e?.status;
  return status === 404 || status === 400;
}

/** Allergies, Health and Medication: editing them also needs sight of them (a platform admin has neither). */
export function canEditSensitiveSections(capabilities: {
  canEdit: boolean;
  canViewSensitive: boolean;
}): boolean {
  return capabilities.canEdit && capabilities.canViewSensitive;
}
